import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { InactivityCase, Milestone } from '../types';
import { InactivityCasePanel, inactivityViewerActs } from './InactivityCasePanel';
import { timelineMilestones, type TimelineContract } from '../utils/timelineContracts';

interface ProjectInactivitySummaryProps {
  contracts: TimelineContract[];
  milestones: Milestone[];
  cases: InactivityCase[];
  viewerSide?: 'person' | 'company' | null;
  onResolved?: (milestone: Milestone) => Promise<void> | void;
  onOpenMilestone?: (milestone: Milestone) => void;
  /**
   * 'waiting': cases the viewer cannot act on, with the status (left side).
   * 'action': cases the viewer acts on, under "Nästa steg" for one contract.
   * Without it, all cases (ClickUp 869fjrytd).
   */
  placement?: 'waiting' | 'action';
  /** With placement 'action': only this contract's cases. */
  contractId?: string;
  /**
   * Stages that already show their own notice (an open objection): a silence
   * case for the same stage is not repeated as a second notice (ClickUp 869fj6n8x).
   */
  excludeMilestoneIds?: ReadonlySet<string>;
}

const urgency = (lifecycleCase: InactivityCase): number => {
  if (lifecycleCase.context.resolutionAvailable === true) return 4;
  if (lifecycleCase.status === 'settling') return 3;
  return Math.min(
    2,
    lifecycleCase.steps.filter(step => step.status === 'completed').length
  );
};

/**
 * Project-level home for active buyer/supplier silence cases. Keeping the
 * notice here prevents a project-critical deadline from being hidden inside
 * one milestone card while still preserving its contract and milestone
 * context.
 */
export function ProjectInactivitySummary({
  contracts,
  milestones,
  cases,
  viewerSide = null,
  onResolved,
  onOpenMilestone,
  placement,
  contractId,
  excludeMilestoneIds
}: ProjectInactivitySummaryProps) {
  const { t } = useTranslation();
  const currentMilestones = useMemo(() => timelineMilestones(contracts, milestones), [contracts, milestones]);
  const rows = useMemo(
    () => cases
      .filter(lifecycleCase =>
        ['seller_silence', 'buyer_silence'].includes(lifecycleCase.caseType) &&
        ['active', 'settling'].includes(lifecycleCase.status)
      )
      .map(lifecycleCase => ({
        lifecycleCase,
        milestone: currentMilestones.find(candidate =>
          candidate.id === lifecycleCase.milestoneId && candidate.contractId === lifecycleCase.contractId
        )
      }))
      .filter((row): row is { lifecycleCase: InactivityCase; milestone: Milestone } =>
        Boolean(row.milestone)
      )
      .filter(row => !placement || inactivityViewerActs(row.lifecycleCase, viewerSide) === (placement === 'action'))
      .filter(row => !contractId || row.milestone.contractId === contractId)
      .filter(row => !excludeMilestoneIds?.has(row.milestone.id))
      .sort((left, right) =>
        urgency(right.lifecycleCase) - urgency(left.lifecycleCase) ||
        new Date(right.lifecycleCase.startedAt).getTime() -
          new Date(left.lifecycleCase.startedAt).getTime()
      ),
    [cases, currentMilestones, placement, viewerSide, contractId, excludeMilestoneIds]
  );

  if (rows.length === 0) return null;

  return (
    <div className={placement === 'action' ? undefined : 'mb-3'} data-testid="project-inactivity-summary">
      {rows.map(({ lifecycleCase, milestone }, index) => {
        const contract = contracts.find(candidate => candidate.id === milestone.contractId);
        return (
          <section
            key={lifecycleCase.id}
            className={index > 0 ? 'mt-2' : undefined}
            data-testid={`project-inactivity-case-${lifecycleCase.id}`}
          >
            <InactivityCasePanel
              milestone={milestone}
              cases={[lifecycleCase]}
              viewerSide={viewerSide}
              onResolved={() => onResolved?.(milestone)}
              onOpenMilestone={onOpenMilestone}
              context={[
                // Under "Nästa steg" the contract is the group's heading already.
                placement !== 'action' && contract
                  ? t('projectView.overview.contractNumber', { number: contract.contractNumber })
                  : null,
                t('projectView.overview.next.stageNumber', { number: milestone.milestoneNumber }),
                milestone.description?.trim() || null
              ].filter(Boolean).join(' · ')}
            />
          </section>
        );
      })}
    </div>
  );
}
